import React, { useState, useEffect, useContext, useRef } from "react";
import "../styles/account.css";
import { useNavigate } from "react-router-dom";
import { AuthContext } from '../context/AuthContext';

const AddBankAccount = () => {
    const [accountNumber, setAccountNumber] = useState("");
    const [accountName, setAccountName] = useState("");
    const [selectedBank, setSelectedBank] = useState(null);
    const [banks, setBanks] = useState([]);
    const [showDropdown, setShowDropdown] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    const [bankSearch, setBankSearch] = useState("");
    const [showBankSearch, setShowBankSearch] = useState(false);

    const bankFieldRef = useRef(null);

    const [saving, setSaving] = useState(false);

    const [loadingName, setLoadingName] = useState(false);

    // 🔴 Field-specific error states
    const [accountNumberError, setAccountNumberError] = useState("");
    const [bankError, setBankError] = useState("");
    const [nameError, setNameError] = useState("");

    const navigate = useNavigate();
    const { getPalmPayBanks, addBankAccount, fetchBankAccounts, queryBankAccountName } = useContext(AuthContext);

    useEffect(() => {
        const fetchBanks = async () => {
            const result = await getPalmPayBanks();
            if (!result.error) {
                setBanks(result.banks);
            } else {
                console.error(result.error);
            }
        };
        fetchBanks();
    }, [getPalmPayBanks]);

    useEffect(() => {
        const fetchAccountName = async () => {
            if (accountNumber.length === 10 && selectedBank) {
                setLoadingName(true);
                setNameError("");

                const result = await queryBankAccountName(selectedBank.bankCode, accountNumber);

                if (result.error) {
                    setAccountName("");
                    setNameError(result.error);
                } else {
                    setAccountName(result.accountName);
                }

                setLoadingName(false);
            } else {
                setAccountName("");
            }
        };

        fetchAccountName();
    }, [accountNumber, selectedBank]);

    const handleSaveAccount = async () => {
        let hasError = false;
        setAccountNumberError("");
        setBankError("");
        setNameError("");

        if (!accountNumber || accountNumber.length !== 10) {
            setAccountNumberError("Enter a valid 10-digit account number.");
            hasError = true;
        }

        if (!selectedBank) {
            setBankError("Please select a bank.");
            hasError = true;
        }

        if (!accountName) {
            setNameError("Account name could not be verified.");
            hasError = true;
        }

        if (hasError) return;

        setSaving(true); // 🔄 Start saving

        const existing = await fetchBankAccounts();
        if (existing?.bankAccounts) {
            const isDuplicate = existing.bankAccounts.some(
                (acc) =>
                    acc.accountNumber === accountNumber &&
                    acc.bankCode === selectedBank.bankCode
            );

            if (isDuplicate) {
                setNameError("This bank account already exists.");
                setSaving(false);
                return;
            }
        }

        const result = await addBankAccount(
            accountName,
            selectedBank.bankName,
            accountNumber,
            selectedBank.bankCode
        );

        if (result.error) {
            setNameError(result.error);
            setSaving(false);
            return;
        }

        setAccountNumber("");
        setAccountName("");
        setSelectedBank(null);
        setNameError("");
        setSaving(false); // ✅ Done saving
        navigate("/bank-account");
    };


    const handleAccountNumberChange = (e) => {
        const input = e.target.value.replace(/\D/g, "");
        if (input.length <= 10) {
            setAccountNumber(input);
        }
    };

    useEffect(() => {
        const onClickOutside = (e) => {
            if (bankFieldRef.current && !bankFieldRef.current.contains(e.target)) {
                setShowBankSearch(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);


    // Filter and sort banks based on search
    const filteredBanks = banks
        .filter(bank =>
            bank.bankName.toLowerCase().includes(bankSearch.toLowerCase())
        )
        .sort((a, b) => {
            const term = bankSearch.toLowerCase();
            const aStarts = a.bankName.toLowerCase().startsWith(term);
            const bStarts = b.bankName.toLowerCase().startsWith(term);
            if (aStarts && !bStarts) return -1;
            if (!aStarts && bStarts) return 1;
            return a.bankName.localeCompare(b.bankName);
        });


    return (
        <div className="bank-account-screen">
            {/* Account Number Field */}
            <div>
                <label className="input-label">Account Number</label>
                <input
                    type="text"
                    value={accountNumber}
                    onChange={handleAccountNumberChange}
                    placeholder="Enter 10-digit account number"
                    className="input-field"
                />
                {accountNumberError && <p className="error-input">{accountNumberError}</p>}
            </div>

            {/* Bank Name Dropdown with Search */}
            <div className="account-adj dropdown-wrapper my-dropdown-wrapper">
                <label className="input-label">Bank Name</label>

                {/* Fake select box */}
                <div
                    className="input-field styled-select"
                    onClick={() => setShowDropdown(!showDropdown)}
                >
                    {selectedBank ? selectedBank.bankName : "Select a bank"}
                </div>
                {bankError && <p className="error-input">{bankError}</p>}

                {/* Custom dropdown popover */}
                {showDropdown && (
                    <div className="my-dropdown-popover">
                        {/* Search Input */}
                        <input
                            type="text"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="my-dropdown-search"
                            autoFocus
                        />

                        {/* Scrollable list */}
                        <div className="my-dropdown-list">
                            {banks
                                .filter((bank) =>
                                    bank.bankName.toLowerCase().includes(searchTerm.toLowerCase())
                                )
                                .map((bank) => (
                                    <div
                                        key={bank.bankCode}
                                        className="my-dropdown-item"
                                        onClick={() => {
                                            setSelectedBank(bank);
                                            setShowDropdown(false);
                                            setBankError("");
                                        }}
                                    >
                                        {bank.bankName}
                                    </div>
                                ))}
                        </div>
                    </div>
                )}
                {bankError && <p className="error-input">{bankError}</p>}
            </div>




            {/* Account Name Field (Read-only) */}
            <div className="account-adj">
                <label className="input-label">Account Name</label>
                <input
                    type="text"
                    value={loadingName ? "Fetching..." : accountName}
                    readOnly
                    placeholder="Account Name"
                    className="input-field styled-select"
                />
                {nameError && <p className="error-input">{nameError}</p>}
            </div>

            <button
                className="save-btn"
                onClick={handleSaveAccount}
                disabled={
                    !accountNumber ||
                    accountNumber.length !== 10 ||
                    !selectedBank ||
                    !accountName ||
                    !!nameError ||
                    loadingName ||
                    saving
                }
            >
                {saving ? "Saving..." : "Save Bank Account"}
            </button>

        </div>
    );
};

export default AddBankAccount;
